<script setup lang="ts">
import { computed } from 'vue';
import type { ComponentGroup, StatusSnapshot } from '@/types';
import {
  componentUptime,
  formatUptime,
  rollupGroupStatus,
  statusLabel,
  statusToneClass,
} from '@/utils/status';
import UptimeBar from './UptimeBar.vue';

const props = defineProps<{
  group: ComponentGroup;
  snapshot: StatusSnapshot;
  uptimeWindowDays: number;
}>();

const groupStatus = computed(() => rollupGroupStatus(props.snapshot, props.group.id));
const groupToneClass = computed(() => statusToneClass(groupStatus.value));
const groupStatusLabel = computed(() => statusLabel(groupStatus.value));
</script>

<template>
  <section class="group-card">
    <header class="group-header" :class="groupToneClass">
      <h2>{{ group.name }}</h2>
      <span class="group-status">{{ groupStatusLabel }}</span>
    </header>
    <ul class="component-list">
      <li
        v-for="component in group.components"
        :key="component.id"
        class="component-row"
        :class="statusToneClass(component.status)"
      >
        <div class="component-meta">
          <span class="component-dot" aria-hidden="true"></span>
          <span class="component-name">{{ component.name }}</span>
        </div>
        <UptimeBar
          :component-id="component.id"
          :history="snapshot.history"
          :window-days="uptimeWindowDays"
        />
        <span class="component-uptime">{{ formatUptime(componentUptime(component)) }}</span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.group-card {
  border: 1px solid var(--xt-border);
  border-radius: 0.5rem;
  background: var(--xt-bg-elevated);
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--xt-border);
  background: var(--xt-tone-bg-soft);
}

.group-header h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--xt-fg-strong);
}

.group-status {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--xt-tone-fg);
}

.component-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.component-row {
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--xt-border-subtle);
}

.component-row:first-child {
  border-top: none;
}

.component-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.component-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--xt-tone-fg);
}

.component-name {
  font-size: 0.9rem;
  color: var(--xt-fg-strong);
}

.component-uptime {
  font-size: 0.8rem;
  color: var(--xt-fg-muted);
  font-variant-numeric: tabular-nums;
  min-width: 4.5rem;
  text-align: right;
}

@media (max-width: 640px) {
  .component-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .component-uptime {
    text-align: left;
  }
}
</style>
